import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BookOpen, Check, ChevronDown, ClipboardCheck, Compass, FlaskConical, Flame, House, LineChart,
  Menu, Monitor, Moon, RotateCcw, ShieldCheck, Star, Sun, X, type LucideIcon,
} from "lucide-react";
import { SITE } from "../config/site";
import { Logo } from "./Logo";
import { AccountMenu } from "./Auth";
import { bootstrapAuth, useAuth } from "../lib/auth";
import { Spinner } from "./ui";
import { ACHIEVEMENT_BY_ID } from "../lib/achievements";
import { dayKey } from "../lib/dates";
import { levelFromXp, liveStreak, type Theme } from "../lib/progress";
import { progressStore, useProgress } from "../lib/store";
import { LANGS, pick, setLang, useT, type Lang, type L } from "../i18n";

const NAV: { to: string; label: L; icon: LucideIcon; end?: boolean; also?: string[] }[] = [
  { to: "/", label: { vi: "Tổng quan", en: "Home" }, icon: House, end: true },
  { to: "/learn", label: { vi: "Lộ trình học", en: "Learning path" }, icon: BookOpen },
  { to: "/exams", label: { vi: "Đề thi", en: "Exams" }, icon: ClipboardCheck },
  { to: "/labs", label: { vi: "Thực hành", en: "Labs" }, icon: FlaskConical },
  { to: "/review", label: { vi: "Ôn tập", en: "Review" }, icon: RotateCcw, also: ["/flashcards"] },
  { to: "/guide", label: { vi: "Cẩm nang", en: "Guide" }, icon: Compass, also: ["/glossary", "/resources"] },
];

/** Chỉ hiện trong menu di động — trên desktop, Tiến độ nằm ở chip streak/cấp độ. */
const MOBILE_EXTRA: typeof NAV = [{ to: "/progress", label: { vi: "Tiến độ", en: "Progress" }, icon: LineChart }];
const ADMIN_NAV: (typeof NAV)[number] = { to: "/admin", label: { vi: "Quản trị", en: "Admin" }, icon: ShieldCheck };

const THEMES: { id: Theme; label: L; hint: L; icon: LucideIcon }[] = [
  { id: "light", label: { vi: "Sáng", en: "Light" }, hint: { vi: "Nền sáng", en: "Light background" }, icon: Sun },
  { id: "dark", label: { vi: "Tối", en: "Dark" }, hint: { vi: "Nền tối, dịu mắt ban đêm", en: "Dark background, easy on the eyes at night" }, icon: Moon },
  { id: "system", label: { vi: "Theo hệ thống", en: "System" }, hint: { vi: "Tự đổi theo máy của bạn", en: "Follows your device setting" }, icon: Monitor },
];

/** Bộ chọn ngôn ngữ VI | EN — luôn hiện, một chạm là đổi. Đề thi vẫn giữ tiếng Anh. */
function LangSwitch({ lang }: { lang: Lang }) {
  const t = useT();
  return (
    <div
      className="lang-switch"
      role="radiogroup"
      aria-label={t("Ngôn ngữ (đề thi luôn bằng tiếng Anh)", "Language (exam questions are always in English)")}
      title={t("Ngôn ngữ giao diện & giải thích — đề thi luôn bằng tiếng Anh", "Interface & explanation language — exam questions stay in English")}
    >
      {LANGS.map((l) => (
        <button key={l.id} type="button" role="radio" aria-checked={l.id === lang} aria-label={l.label} onClick={() => setLang(l.id)}>
          {l.short}
        </button>
      ))}
    </div>
  );
}

function setTheme(theme: Theme): void {
  progressStore.update((p) => ({ ...p, theme }));
}

/** Nút chọn giao diện: hiện rõ chế độ đang dùng, bấm mở menu 3 lựa chọn có mô tả. */
function ThemeMenu({ theme, lang }: { theme: Theme; lang: Lang }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = THEMES.find((x) => x.id === theme) ?? THEMES[2]!;
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const CurIcon = current.icon;
  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className="theme-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t(`Giao diện: ${pick(current.label, lang)}. Bấm để chọn`, `Theme: ${pick(current.label, lang)}. Click to change`)}
        onClick={() => setOpen((o) => !o)}
      >
        <CurIcon size={18} aria-hidden="true" />
        <span className="theme-btn-label">{pick(current.label, lang)}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="menu" role="menu" aria-label={t("Chọn giao diện", "Choose theme")}>
          <div className="menu-title">{t("Giao diện", "Theme")}</div>
          {THEMES.map((th) => {
            const Icon = th.icon;
            return (
              <button
                key={th.id}
                type="button"
                role="menuitemradio"
                aria-checked={th.id === theme}
                className="menu-item"
                onClick={() => { setTheme(th.id); setOpen(false); }}
              >
                <Icon size={18} aria-hidden="true" />
                <span className="grow"><strong>{pick(th.label, lang)}</strong><span className="tiny muted">{pick(th.hint, lang)}</span></span>
                {th.id === theme && <Check size={18} aria-hidden="true" className="menu-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Bộ chọn giao diện dạng 3 nút ngang — dùng trong menu di động. */
function ThemeSegmented({ theme, lang }: { theme: Theme; lang: Lang }) {
  const t = useT();
  return (
    <div className="segmented" role="radiogroup" aria-label={t("Giao diện", "Theme")}>
      {THEMES.map((th) => {
        const Icon = th.icon;
        return (
          <button key={th.id} type="button" role="radio" aria-checked={th.id === theme} onClick={() => setTheme(th.id)}>
            <Icon size={18} aria-hidden="true" /> {th.id === "system" ? t("Tự động", "Auto") : pick(th.label, lang)}
          </button>
        );
      })}
    </div>
  );
}

function resolveTheme(t: Theme): "light" | "dark" {
  if (t !== "system") return t;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function useApplyTheme(theme: Theme): void {
  useEffect(() => {
    const apply = () => document.documentElement.setAttribute("data-theme", resolveTheme(theme));
    apply();
    if (theme !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);
}

interface Toast { key: number; icon: LucideIcon; text: string }

function useAchievementToasts(): Toast[] {
  const [toasts, setToasts] = useState<Toast[]>([]);
  useEffect(() => {
    return progressStore.subscribe(() => {
      const ids = progressStore.takeUnlocked();
      if (!ids.length) return;
      const fresh = ids.flatMap((id) => {
        const a = ACHIEVEMENT_BY_ID.get(id);
        if (!a) return [];
        const lang = progressStore.get().lang;
        const text = lang === "en" ? `Badge unlocked: ${a.title.en}` : `Mở khóa huy hiệu: ${a.title.vi}`;
        return [{ key: Date.now() + Math.random(), icon: a.icon, text }];
      });
      setToasts((t) => [...t, ...fresh]);
      for (const f of fresh) setTimeout(() => setToasts((t) => t.filter((x) => x.key !== f.key)), 4500);
    });
  }, []);
  return toasts;
}

export function Layout() {
  const progress = useProgress();
  const t = useT();
  const lang = progress.lang;
  const location = useLocation();
  useApplyTheme(progress.theme);
  const toasts = useAchievementToasts();
  const auth = useAuth();
  const navItems = auth.user?.isAdmin ? [...NAV, ADMIN_NAV] : NAV;
  useEffect(() => { void bootstrapAuth(); }, []);
  const streak = liveStreak(progress.streak, dayKey());
  const lvl = levelFromXp(progress.xp);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const isActive = (n: (typeof NAV)[number]) => n.also?.some((p) => location.pathname.startsWith(p)) ?? false;

  return (
    <div className="app">
      <a href="#main" className="skip-link">{t("Bỏ qua tới nội dung", "Skip to content")}</a>
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" aria-label={`${SITE.name} — ${t("trang chủ", "home")}`}>
            <Logo size={36} />
            <span className="brand-text">
              <span className="brand-name">{SITE.name}</span>
              <span className="brand-sub">by {SITE.owner}</span>
            </span>
          </Link>
          <nav className="nav" aria-label={t("Điều hướng chính", "Main navigation")}>
            {navItems.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive: a }) => (a || isActive(n) ? "active" : undefined)}>
                  <Icon size={18} aria-hidden="true" />
                  <span>{pick(n.label, lang)}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="topbar-right">
            <NavLink
              to="/progress"
              className={({ isActive: a }) => `stat-chip progress-chip${a ? " active" : ""}`}
              aria-label={t(`Tiến độ: chuỗi ${streak} ngày, cấp ${lvl.level}`, `Progress: ${streak}-day streak, level ${lvl.level}`)}
              title={t(`Tiến độ — chuỗi ${streak} ngày (kỷ lục ${progress.streak.best}) · ${progress.xp} XP, còn ${lvl.need - lvl.into} XP để lên cấp`, `Progress — ${streak}-day streak (best ${progress.streak.best}) · ${progress.xp} XP, ${lvl.need - lvl.into} XP to next level`)}
            >
              <span className="streak"><Flame size={18} aria-hidden="true" /> <strong>{streak}</strong></span>
              <span className="sep" aria-hidden="true" />
              <span className="level"><Star size={18} aria-hidden="true" /> <strong>{lvl.level}</strong></span>
            </NavLink>
            <LangSwitch lang={lang} />
            <span className="desktop-only"><ThemeMenu theme={progress.theme} lang={lang} /></span>
            <AccountMenu />
            <button
              type="button"
              className="icon-btn menu-toggle"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? t("Đóng menu", "Close menu") : t("Mở menu", "Open menu")}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="mobile-nav" id="mobile-nav">
            <nav aria-label={t("Điều hướng (di động)", "Navigation (mobile)")}>
              {[...navItems, ...MOBILE_EXTRA].map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive: a }) => (a || isActive(n) ? "active" : undefined)}>
                    <Icon size={20} aria-hidden="true" />
                    <span>{pick(n.label, lang)}</span>
                  </NavLink>
                );
              })}
            </nav>
            <div className="mobile-theme">
              <span className="label">{t("Giao diện", "Theme")}</span>
              <ThemeSegmented theme={progress.theme} lang={lang} />
            </div>
          </div>
        )}
      </header>

      <main id="main">
        <div className="container">
          {auth.status === "loading" || auth.status === "idle" ? (
            <Spinner label={t("Đang tải…", "Loading…")} />
          ) : auth.status === "error" ? (
            <div className="callout bad" role="alert">
              {t("Không kết nối được máy chủ. Vui lòng thử lại sau.", "Couldn't reach the server. Please try again later.")}{" "}
              <button type="button" className="btn sm" onClick={() => window.location.reload()}>{t("Thử lại", "Retry")}</button>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="container row between">
          <span>
            {lang === "en"
              ? <>An independent study project, <strong>not</strong> affiliated with or endorsed by Anthropic. Questions are original, written from the public exam guides — not real exam questions.</>
              : <>Dự án học tập độc lập, <strong>không</strong> liên kết hay được Anthropic xác nhận. Câu hỏi là nội dung tự biên soạn dựa trên exam guide công khai — không phải câu hỏi thi thật.</>}
          </span>
          <span><Link to="/feedback">{t("Góp ý & báo lỗi", "Feedback")}</Link> · <Link to="/privacy">{t("Quyền riêng tư", "Privacy")}</Link> · © {SITE.startYear} {SITE.owner} · {SITE.domain}</span>
        </div>
      </footer>

      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => {
          const Icon = t.icon;
          return <div className="toast" key={t.key}><Icon size={20} aria-hidden="true" />{t.text}</div>;
        })}
      </div>
    </div>
  );
}
